import * as uniloc from "uniloc";
import { Err } from "../../../error";
import { IRouteData, IRouterState, receiveData, receiveError } from "./reducer";
import { AnyAction } from "redux";
import config from "../../../config";
import { IGcpAppDispatch } from "../../store";
import { getRouterState, IStoreState } from "../../rootReducer";

// #region ROUTES

export const VIEWMODE_INIT = "VIEWMODE_INIT";
export const VIEWMODE_ADD = "VIEWMODE_ADD";
export const VIEWMODE_MANAGE = "VIEWMODE_MANAGE";
export const VIEWMODE_GCPREQUEST = "VIEWMODE_GCPREQUEST";
export const VIEWMODE_QC = "VIEWMODE_QC";
export const VIEWMODE_READONLY = "VIEWMODE_READONLY";
export const VIEWMODE_READONLY_NOSCAN = "VIEWMODE_READONLY_NOSCAN";
export const VIEWMODE_PILOT = "VIEWMODE_PILOT";
export const VIEWMODE_CURRENT_GCP_REQUEST = "VIEWMODE_CURRENT_GCP_REQUEST";

const { lookup, generate } = uniloc({
  [VIEWMODE_INIT]: "GET /customers/:customerId/sites/:siteId/init",
  [VIEWMODE_ADD]: "GET /customers/:customerId/sites/:siteId/add",
  [VIEWMODE_MANAGE]: "GET /customers/:customerId/sites/:siteId/manage",
  [VIEWMODE_GCPREQUEST]:
    "GET /customers/:customerId/sites/:siteId/scans/:scanDate/gcprequest",
  [VIEWMODE_QC]: "GET /customers/:customerId/sites/:siteId/scans/:scanDate/qc",
  [VIEWMODE_READONLY]:
    "GET /customers/:customerId/sites/:siteId/scans/:scanDate/readonly", // deprecated, will redirect to VIEWMODE_CURRENT_GCP_REQUEST
  [VIEWMODE_READONLY_NOSCAN]:
    "GET /customers/:customerId/sites/:siteId/readonly", // deprecated, will redirect to VIEWMODE_CURRENT_GCP_REQUEST
  [VIEWMODE_PILOT]: "GET /customers/:customerId/sites/:siteId/pilot",
  [VIEWMODE_CURRENT_GCP_REQUEST]:
    "GET /customers/:customerId/sites/:siteId/current-gcp-request",
});

interface IRedirectInfo {
  mode: string;
  keepScanDate?: boolean;
}

const redirects: {
  [key: string]: IRedirectInfo;
} = {
  [VIEWMODE_READONLY]: {
    mode: VIEWMODE_CURRENT_GCP_REQUEST,
    keepScanDate: false,
  },
  [VIEWMODE_READONLY_NOSCAN]: { mode: VIEWMODE_CURRENT_GCP_REQUEST },
};

export const lookupRoute = lookup;

export interface IReadonlyNoScanRouteProps {
  customerId: string;
  siteId: string;
  returnUrl?: string;
  from?: string;
}

export interface IReadonlyRouteProps {
  customerId: string;
  siteId: string;
  scanDate: string;
  returnUrl?: string;
  from?: string;
}

export interface ICurrentGCPRequestRouteProps {
  customerId: string;
  siteId: string;
  returnUrl?: string;
  from?: string;
}

export interface IPilotRouteProps {
  customerId: string;
  siteId: string;
  returnUrl?: string;
  from?: string;
}

export const generateReadonlyRoute =
  (scanDate: string) =>
  (dispatch: IGcpAppDispatch): string => {
    return dispatch(generateRoute(VIEWMODE_READONLY, scanDate));
  };

export const generateAddRoute =
  () =>
  (dispatch: IGcpAppDispatch): string => {
    return dispatch(generateRoute(VIEWMODE_ADD));
  };

export const generateManageRoute =
  () =>
  (dispatch: IGcpAppDispatch): string => {
    return dispatch(generateRoute(VIEWMODE_MANAGE));
  };

export const generateRedirectURL =
  (redirectInfo: IRedirectInfo) =>
  (dispatch: IGcpAppDispatch): string => {
    const { mode, keepScanDate } = redirectInfo;

    return dispatch(
      generateRoute(mode, keepScanDate === false ? null : undefined),
    );
  };

const generateRoute =
  (mode: string, scanDateArg?: string | null) =>
  (dispatch: IGcpAppDispatch, getState: () => IStoreState): string => {
    const routerState = getRouterState(getState());
    const { customerId, siteId, scanDate, returnUrl, from } = routerState.data;
    const obj: Record<string, string> = {
      customerId,
      siteId,
      scanDate: scanDateArg === null ? null : scanDateArg || scanDate,
      return_url: returnUrl,
      from,
    };
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) {
        continue;
      }
      if (!obj[key]) {
        delete obj[key];
      }
    }

    return generate(mode, obj);
  };

export const putRoute = (routeUrl: string): AnyAction => {
  const unilocData = lookup(routeUrl);
  if (unilocData && unilocData.name) {
    const { customerId, siteId, scanDate, return_url, from } =
      unilocData.options;
    return receiveData({
      customerId,
      siteId,
      scanDate,
      returnUrl: return_url,
      from,
      viewMode: unilocData.name,
    });
  }
  return receiveError({
    code: "ROUTE_NOT_FOUND",
    message: "Not Found",
  });
};

export function getRouteDataFromUrl(url: string): IRouteData {
  const { options, name } = lookup(extractUriRoute(url));
  const { customerId, siteId, scanDate, return_url, from } = options;
  if (!customerId || !siteId || !name) {
    throw new Err("Not Found");
  }
  return {
    customerId,
    siteId,
    scanDate: scanDate || null,
    returnUrl: return_url ? decodeURIComponent(return_url) : null,
    from: from || null,
    viewMode: name,
  };
}

const filterObject = (
  o: { [key: string]: any },
  f: (key: string, value: any) => boolean,
) =>
  Object.keys(o).reduce(
    (acc, curr) => (f(curr, o[curr]) ? { ...acc, [curr]: o[curr] } : acc),
    {},
  );

export const restoreLocation = (routerState: IRouterState) => {
  return (
    config.endpoints.gcpApp +
    "/#" +
    generate(
      routerState.data.viewMode,
      filterObject(
        routerState.data,
        (key, value) => key !== "viewMode" && !!value,
      ),
    )
  );
};

export function extractUriRoute(uri: string): string {
  const hashIndex = uri.indexOf("#");
  return hashIndex > -1 ? uri.slice(hashIndex + 1) : "/";
}

export function getRedirectForViewMode(
  routerState: IRouterState,
): IRedirectInfo | null {
  if (routerState.data.viewMode in redirects) {
    return redirects[routerState.data.viewMode];
  }

  return null;
}
